import { apiRequest } from "./client";
import type { ExploreTreeDto, PaginatedList, SharedTreeDto, TreeDto, TreeSummaryDto } from "./types";

export type CreateTreePayload = {
	title: string;
	description?: string;
	visibility?: string;
	commentsEnabled?: boolean;
	coverImageUrl?: string;
	coverImageName?: string;
};

export type GetMyTreesParams = {
	search?: string;
	visibility?: string;
	pageIndex?: number;
	pageSize?: number;
};

export type ExploreTreesParams = {
	search?: string;
	/** Future feed tabs: "Trending" | "New" | "ForYou". Omit for the default latest feed. */
	feed?: string;
	pageIndex?: number;
	pageSize?: number;
};

export type UserPublicTreesParams = {
	search?: string;
	pageIndex?: number;
	pageSize?: number;
};

export type UpdateTreePayload = {
	title: string;
	description?: string;
	commentsEnabled?: boolean;
	coverImageUrl?: string;
	coverImageName?: string;
};

export const createTree = (payload: CreateTreePayload, token: string): Promise<TreeDto> =>
	apiRequest<TreeDto>("/trees", { method: "POST", body: payload, token });

/** Updates a tree's title, description, cover image, and comment setting. Requires Admin on the tree. */
export const updateTree = (id: string, payload: UpdateTreePayload, token: string): Promise<TreeDto> =>
	apiRequest<TreeDto>(`/trees/${id}`, { method: "PUT", body: { treeId: id, ...payload }, token });

/** Changes a tree's visibility (Draft/Private/Public). Switching to Private requires Pro. Requires Admin. */
export const changeTreeVisibility = (id: string, visibility: string, token: string): Promise<TreeDto> =>
	apiRequest<TreeDto>(`/trees/${id}/visibility`, {
		method: "PATCH",
		body: { treeId: id, visibility },
		token
	});

/** Trees other users own that are shared with the caller via an active membership. */
export const getSharedWithMe = (token: string): Promise<SharedTreeDto[]> =>
	apiRequest<SharedTreeDto[]>("/trees/shared-with-me", { token });

export const getTreeById = (id: string, token: string): Promise<TreeDto> =>
	apiRequest<TreeDto>(`/trees/${id}`, { token });

/**
 * Soft-deletes a tree; only the owner may call it. The row is flagged rather than removed, so a
 * deleted tree stays recoverable by an admin — nothing here destroys the tree or its entities.
 */
export const deleteTree = (id: string, token: string): Promise<void> =>
	apiRequest<void>(`/trees/${id}`, { method: "DELETE", token });

export const starTree = (id: string, token: string): Promise<void> =>
	apiRequest<void>(`/trees/${id}/star`, { method: "POST", token });

export const unstarTree = (id: string, token: string): Promise<void> =>
	apiRequest<void>(`/trees/${id}/star`, { method: "DELETE", token });

export const likeTree = (id: string, token: string): Promise<void> =>
	apiRequest<void>(`/trees/${id}/like`, { method: "POST", token });

export const unlikeTree = (id: string, token: string): Promise<void> =>
	apiRequest<void>(`/trees/${id}/like`, { method: "DELETE", token });
/** Explore feed: all public trees plus the caller's own trees, searchable by tree or entity name. */
export const exploreTrees = (params: ExploreTreesParams, token: string): Promise<PaginatedList<ExploreTreeDto>> => {
	const searchParams = new URLSearchParams();

	if (params.search) {
		searchParams.set("Search", params.search);
	}
	if (params.feed) {
		searchParams.set("Feed", params.feed);
	}
	if (params.pageIndex !== undefined) {
		searchParams.set("PageIndex", params.pageIndex.toString());
	}
	if (params.pageSize !== undefined) {
		searchParams.set("PageSize", params.pageSize.toString());
	}

	const query = searchParams.toString();

	return apiRequest<PaginatedList<ExploreTreeDto>>(query ? `/trees/explore?${query}` : "/trees/explore", {
		token
	});
};

/** Public trees owned by one user, for their public profile page. Readable anonymously. */
export const getUserPublicTrees = (
	userId: string,
	params: UserPublicTreesParams,
	token: string
): Promise<PaginatedList<ExploreTreeDto>> => {
	const searchParams = new URLSearchParams();

	if (params.search) {
		searchParams.set("Search", params.search);
	}
	if (params.pageIndex !== undefined) {
		searchParams.set("PageIndex", params.pageIndex.toString());
	}
	if (params.pageSize !== undefined) {
		searchParams.set("PageSize", params.pageSize.toString());
	}

	const query = searchParams.toString();

	return apiRequest<PaginatedList<ExploreTreeDto>>(
		query ? `/trees/by-user/${userId}?${query}` : `/trees/by-user/${userId}`,
		{ token }
	);
};

export const getMyTrees = (params: GetMyTreesParams, token: string): Promise<PaginatedList<TreeSummaryDto>> => {
	const searchParams = new URLSearchParams();

	if (params.search) {
		searchParams.set("Search", params.search);
	}
	if (params.visibility) {
		searchParams.set("Visibility", params.visibility);
	}
	if (params.pageIndex !== undefined) {
		searchParams.set("PageIndex", params.pageIndex.toString());
	}
	if (params.pageSize !== undefined) {
		searchParams.set("PageSize", params.pageSize.toString());
	}

	const query = searchParams.toString();

	return apiRequest<PaginatedList<TreeSummaryDto>>(query ? `/trees?${query}` : "/trees", { token });
};
